import React from "react";
import {
  FileCheck2, TrendingUp, Percent, DollarSign, Search, ScrollText, Sparkles, AlertTriangle,
  CheckCircle2, ArrowUp, ArrowDown, Minus,
} from "lucide-react";

/** Reusable formatters + presentation helpers for a parsed credit report. */

export function fmtMoney(n) {
  if (n === null || n === undefined) return "—";
  return "$" + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
}
export function fmtPct(n) {
  if (n === null || n === undefined) return "—";
  return `${Number(n).toFixed(1)}%`;
}
export function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", year: "numeric" }); }
  catch { return iso; }
}
export function scoreBand(s) {
  if (!s) return { label: "—", color: "#94a3b8" };
  if (s >= 800) return { label: "Excellent", color: "#10b981" };
  if (s >= 740) return { label: "Very Good", color: "#22c55e" };
  if (s >= 670) return { label: "Good", color: "#38bdf8" };
  if (s >= 580) return { label: "Fair", color: "#f59e0b" };
  return { label: "Poor", color: "#ef4444" };
}

/** Given two report objects (newest, previous), return per-bureau score deltas. */
export function computeScoreDeltas(newest, previous) {
  if (!newest || !previous) return null;
  const prevByBureau = {};
  (previous?.data?.scores || []).forEach((s) => {
    const k = (s.bureau || "unknown").toLowerCase();
    if (!prevByBureau[k]) prevByBureau[k] = s.score;
  });
  const out = [];
  for (const s of (newest?.data?.scores || [])) {
    const k = (s.bureau || "unknown").toLowerCase();
    const prev = prevByBureau[k];
    if (prev !== undefined && s.score !== undefined) {
      out.push({ bureau: s.bureau, from: prev, to: s.score, delta: (s.score || 0) - (prev || 0) });
    }
  }
  return out;
}

export function ScoreDeltaChips({ deltas }) {
  if (!deltas || deltas.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid="score-delta-chips">
      {deltas.map((d, i) => {
        const up = d.delta > 0;
        const flat = d.delta === 0;
        const Icon = flat ? Minus : up ? ArrowUp : ArrowDown;
        const tone = flat ? "bg-slate-500/20 text-slate-300" : up ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300";
        return (
          <div key={i} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${tone}`}>
            <Icon size={12} />
            <span className="uppercase tracking-widest text-[10px] font-bold opacity-80">{d.bureau}</span>
            <span>{d.from} → {d.to}</span>
            <span className="font-bold">{d.delta > 0 ? `+${d.delta}` : d.delta}</span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Full report card. `theme="dark"` (Funding Center) or `theme="light"` (Admin panel).
 */
export default function CreditReportView({ report, theme = "dark", deltas = null }) {
  if (!report) return null;
  const d = report.data || {};
  const scores = d.scores || [];
  const s = d.summary || {};
  const tradelines = d.tradelines || [];
  const inquiries = d.inquiries || [];
  const collections = d.collections || [];

  const dark = theme === "dark";
  const card = dark ? "glass rounded-2xl p-5" : "bg-white rounded-2xl p-5 border border-slate-100";
  const heading = dark ? "text-white" : "text-slate-900";
  const sub = dark ? "text-slate-400" : "text-slate-500";
  const chip = dark ? "bg-black/30 border border-white/5" : "bg-slate-50 border border-slate-100";
  const strong = dark ? "text-white" : "text-slate-900";

  return (
    <div className="space-y-4" data-testid="credit-report-view">
      {/* Header */}
      <div className={`${card} flex items-center gap-3 flex-wrap`}>
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white shadow-lg shadow-amber-500/30">
          <FileCheck2 size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">{dark ? "Client's real credit data" : "Parsed credit report"}</p>
          <h3 className={`font-display text-xl font-black ${heading}`}>{d.source_hint || d.bureau || "Credit Report"}</h3>
          <p className={`text-xs mt-0.5 ${sub}`}>Uploaded {fmtDate(report.created_at)}{d.as_of_date ? ` · report dated ${fmtDate(d.as_of_date)}` : ""}</p>
        </div>
        {deltas && deltas.length > 0 && <ScoreDeltaChips deltas={deltas} />}
      </div>

      {/* Scores */}
      {scores.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {scores.slice(0, 3).map((sc, i) => {
            const band = scoreBand(sc.score);
            return (
              <div key={i} className={`${card} relative overflow-hidden`}>
                <div className="absolute inset-0 opacity-20" style={{ background: `radial-gradient(circle at top right, ${band.color}, transparent 60%)` }} />
                <p className={`text-[10px] uppercase tracking-widest font-bold ${sub}`}>{(sc.bureau || "Bureau").toString()}</p>
                <p className="font-display text-5xl font-black mt-1" style={{ color: band.color }}>{sc.score || "—"}</p>
                <p className={`text-sm font-bold mt-1 ${strong}`}>{band.label}</p>
                <p className={`text-xs mt-0.5 ${sub}`}>{sc.model || "FICO"} {sc.as_of ? `· ${fmtDate(sc.as_of)}` : ""}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Metric card={card} sub={sub} strong={strong} icon={ScrollText} label="Open accounts" value={s.open_accounts ?? "—"} />
        <Metric card={card} sub={sub} strong={strong} icon={DollarSign} label="Total balance" value={fmtMoney(s.total_balance)} />
        <Metric card={card} sub={sub} strong={strong} icon={Percent} label="Utilization" value={fmtPct(s.utilization_pct)} tint={s.utilization_pct > 30 ? "text-red-400" : "text-emerald-400"} />
        <Metric card={card} sub={sub} strong={strong} icon={Search} label="Hard inquiries (24mo)" value={s.hard_inquiries_24mo ?? "—"} tint={(s.hard_inquiries_24mo || 0) > 4 ? "text-amber-400" : "text-emerald-400"} />
      </div>

      {/* Readiness notes */}
      {Array.isArray(d.funding_readiness_notes) && d.funding_readiness_notes.length > 0 && (
        <div className={card}>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={16} className="text-amber-400" />
            <h4 className={`font-display font-bold ${heading}`}>Funding readiness</h4>
          </div>
          <ul className="space-y-1.5">
            {d.funding_readiness_notes.map((n, i) => (
              <li key={i} className={`flex gap-2 text-sm ${dark ? "text-slate-200" : "text-slate-700"}`}>
                <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-emerald-500" /> {n}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Tradelines */}
      {tradelines.length > 0 && (
        <div className={card}>
          <h4 className={`font-display font-bold mb-3 ${heading}`}>Tradelines · {tradelines.length}</h4>
          <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1">
            {tradelines.map((t, i) => (
              <div key={i} className={`rounded-xl p-3 ${chip}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <p className={`font-semibold truncate ${strong}`}>{t.creditor || "—"}</p>
                    <p className={`text-[11px] ${sub}`}>{t.account_type || "Account"} · Opened {fmtDate(t.opened_date)}</p>
                  </div>
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${(t.status || "").toLowerCase().includes("open") ? "bg-emerald-500/20 text-emerald-500" : "bg-slate-500/20 text-slate-500"}`}>
                    {t.status || "—"}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                  <Mini sub={sub} strong={strong} label="Balance" value={fmtMoney(t.balance)} />
                  <Mini sub={sub} strong={strong} label="Limit" value={fmtMoney(t.credit_limit)} />
                  <Mini sub={sub} strong={strong} label="Util" value={fmtPct(t.utilization_pct)} tint={t.utilization_pct > 30 ? "text-red-500" : "text-emerald-500"} />
                </div>
                {t.past_due > 0 && <p className="text-[11px] text-red-500 mt-1">⚠ Past due: {fmtMoney(t.past_due)}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Inquiries + collections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {inquiries.length > 0 && (
          <div className={card}>
            <h4 className={`font-display font-bold mb-2 ${heading}`}>Recent inquiries · {inquiries.length}</h4>
            <div className="space-y-1 max-h-[280px] overflow-y-auto">
              {inquiries.slice(0, 12).map((q, i) => (
                <div key={i} className={`flex items-center justify-between text-sm py-1.5 ${dark ? "border-b border-white/5" : "border-b border-slate-100"} last:border-0`}>
                  <span className={strong}>{q.creditor}</span>
                  <span className={`text-xs ${sub}`}>{fmtDate(q.date)} · <span className={q.type === "hard" ? "text-amber-400" : "text-slate-500"}>{q.type}</span></span>
                </div>
              ))}
            </div>
          </div>
        )}
        {collections.length > 0 && (
          <div className={`${card} border-red-500/20`}>
            <h4 className={`font-display font-bold mb-2 flex items-center gap-2 ${heading}`}>
              <AlertTriangle size={16} className="text-red-500" /> Collections · {collections.length}
            </h4>
            <div className="space-y-2">
              {collections.map((c, i) => (
                <div key={i} className="rounded-lg bg-red-500/10 border border-red-500/20 p-3 text-sm">
                  <p className={`font-semibold ${strong}`}>{c.creditor}</p>
                  <p className={`text-xs ${sub}`}>{fmtMoney(c.balance)} · {c.status || "In collections"}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Metric({ card, sub, strong, icon: Icon, label, value, tint }) {
  return (
    <div className={card}>
      <div className={`flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold mb-1 ${sub}`}>
        <Icon size={12} /> {label}
      </div>
      <p className={`font-display text-2xl font-black ${tint || strong}`}>{value}</p>
    </div>
  );
}
function Mini({ sub, strong, label, value, tint }) {
  return (
    <div>
      <p className={`text-[10px] uppercase tracking-widest font-bold ${sub}`}>{label}</p>
      <p className={`font-semibold ${tint || strong}`}>{value}</p>
    </div>
  );
}
